import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Spinner,
  Center,
  SimpleGrid
} from '@chakra-ui/react';
import { useFormDetails } from '../../queries/useUserQueries';

// Component to display a simple form field value
const FieldValue = ({ value, type = "text" }) => {
  if (!value) return <Text color="gray.500">Not provided</Text>;

  if (type === 'date') {
    try {
      const date = new Date(value);
      return <Text>{date.toLocaleDateString()}</Text>;
    } catch (e) {
      return <Text>{value}</Text>;
    }
  }

  return <Text>{value}</Text>;
};

// Process responses into a more usable format
const processResponses = (responses, formDetails) => {
  // Group by section ID
  const sectionMap = {};
  
  // First, setup sections from form details
  if (formDetails && formDetails.sections) {
    formDetails.sections.forEach(section => {
      sectionMap[section._id] = {
        sectionId: section._id,
        title: section.title,
        isTable: section.isTable || false,
        fields: {},
        rows: {}
      };
      
      // Map fields by ID
      section.questions.forEach(question => {
        sectionMap[section._id].fields[question._id] = {
          label: question.label || question.text,
          type: question.type || 'text'
        };
      });
    });
  }
  
  // Now add responses
  responses.forEach(response => {
    const { formSectionId, formFieldId, formRowIndex, value } = response;
    
    // Initialize section if it doesn't exist yet (this would be odd, but let's be safe)
    if (!sectionMap[formSectionId]) {
      sectionMap[formSectionId] = {
        sectionId: formSectionId,
        title: `Section (ID: ${formSectionId.slice(-6)})`,
        fields: {},
        rows: {}
      };
    }
    
    // Initialize row if it doesn't exist yet
    if (!sectionMap[formSectionId].rows[formRowIndex]) {
      sectionMap[formSectionId].rows[formRowIndex] = [];
    }
    
    // Add the field to the appropriate row
    sectionMap[formSectionId].rows[formRowIndex].push({
      fieldId: formFieldId,
      value,
      label: sectionMap[formSectionId].fields[formFieldId]?.label || `Field (ID: ${formFieldId.slice(-6)})`,
      type: sectionMap[formSectionId].fields[formFieldId]?.type || 'text'
    });
  });
  
  // Convert map to array and sort by section order
  return Object.values(sectionMap);
};

// Component to fetch and display form schema to show the actual field labels
const UserDetailsViewer = ({ userDetails, isOpen, onClose }) => {
  const [processedData, setProcessedData] = useState([]);
  
  // Fetch form details using the formId from userDetails
  const formId = userDetails?.formId;
  const { data: formDetails, isLoading: isFormLoading } = useFormDetails(formId);
  
  useEffect(() => {
    if (userDetails?.responses) {
      const data = processResponses(userDetails.responses, formDetails);
      setProcessedData(data);
    }
  }, [userDetails, formDetails]);
  
  if (!userDetails) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="xl"
      scrollBehavior="inside"
      blockScrollOnMount={true}
      isCentered={true}
    >
      <ModalOverlay />
      <ModalContent 
        maxW="70%" 
        maxH="90vh" 
        h="auto" 
        m="auto"
      >
        <ModalHeader bg="#5D38DB" color="white">
          <Heading as="h2" size="md">
            User Details
          </Heading>
          <ModalCloseButton color="white" />
        </ModalHeader>
        <ModalBody pt={4} px={4} pb={4} overflowY="auto">
          {isFormLoading ? (
            <Center py={10}>
              <Spinner size="xl" color="#5D38DB" />
            </Center>
          ) : (
            <Stack spacing={4}>
              {processedData.map((section) => (
                <Card key={`section-${section.sectionId}`} shadow="sm">
                  <CardHeader py={2} px={4} bg="gray.50">
                    <Heading as="h3" size="sm">
                      {section.title}
                    </Heading>
                  </CardHeader>
                  <CardBody py={3} px={4}>
                    {/* Display non-table sections (single row) */}
                    {Object.keys(section.rows).length === 1 && section.rows[0] && !section.isTable && (
                      <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                        {section.rows[0].map((field) => (
                          <Box key={`field-${field.fieldId}`} borderBottom="1px" borderColor="gray.200" pb={2}>
                            <Text fontWeight="bold" fontSize="sm">{field.label}</Text>
                            <FieldValue 
                              value={field.value} 
                              type={field.type}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}
                    
                    {/* Display table sections (multiple rows or marked as table) */}
                    {(Object.keys(section.rows).length > 1 || section.isTable) && (
                      <Box overflowX="auto">
                        <Table 
                          variant="simple" 
                          size="sm" 
                          borderWidth="1px" 
                          borderColor="gray.300"
                        >
                          <Thead>
                            <Tr>
                              {/* Get fields from first row to use as headers */}
                              {Object.values(section.rows)[0].map((field) => (
                                <Th key={`th-${field.fieldId}`} fontSize="xs">
                                  {field.label}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(section.rows).map(([rowIndex, fields]) => (
                              <Tr key={`row-${rowIndex}`}>
                                {fields.map((field) => (
                                  <Td key={`cell-${rowIndex}-${field.fieldId}`}>
                                    <FieldValue 
                                      value={field.value} 
                                      type={field.type}
                                    />
                                  </Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </CardBody>
                </Card>
              ))}
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserDetailsViewer; 